import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ISignUpCrmParams } from '@aqx-core/common/api/auth';
import { useSignupCrm } from '@aqx-core/common/services/sign-up-crm';
import { signUpCrmViewController } from '@aqx-core/common/services/sign-up-crm/controller';
import { EUISnackBarMessageType, snackbarController } from '@aqx-core/common/services/snackbar';

import { useRequestOtpTimer } from './use-request-otp-timer';

const OTP_LENGTH = 6;

export const useSignUpVerification = (onSubmitSuccess?: (params: ISignUpCrmParams) => void) => {
  const { t } = useTranslation();
  const { loading: isLoading, basicInfo, otpTimeoutInSec, otpExpiryTime } = useSignupCrm();

  const { startTimer, remainingTimeText, isResendDisabled } = useRequestOtpTimer({
    isSubmitting: isLoading,
    identifier: basicInfo?.email || '',
    durationSeconds: otpTimeoutInSec
  });

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');

  const onClickResend = useCallback(() => {
    if (!isResendDisabled) {
      signUpCrmViewController.requestOtp({
        onSuccess: (timeoutInSec, expiryTime) => {
          startTimer(timeoutInSec, expiryTime);
        },
        onError: (message: string) =>
          snackbarController.showAlert({
            message,
            messageType: EUISnackBarMessageType.ERROR
          })
      });
    }
  }, [isResendDisabled, startTimer]);

  const onOtpChange = (pin: string) => {
    setOtp(pin);
    setError('');
  };

  const onSubmit = async () => {
    if (otp.length !== OTP_LENGTH) {
      setError(t('Please input a valid 6-digit code'));
      return;
    }
    const params = { otp };
    const onError = (message: string) => {
      setError(message);
    };

    signUpCrmViewController.completeSignUp({ params, onError, onSuccess: onSubmitSuccess });
  };

  const isButtonDisabled = useMemo(() => {
    return otp.length !== OTP_LENGTH;
  }, [otp]);

  const initialOtpRef = useRef({ otpTimeoutInSec, otpExpiryTime });

  useEffect(() => {
    const { otpTimeoutInSec: timeout, otpExpiryTime: expiry } = initialOtpRef.current;
    startTimer(timeout, expiry);
  }, [startTimer]);

  return {
    isLoading,
    onOtpChange,
    remainingTimeText,
    isResendDisabled,
    onSubmit,
    onClickResend,
    error,
    isButtonDisabled,
    otpTimeoutInSec
  };
};
